import type { SheetCell, ViewQueryRecordNode, ViewQueryRecordNodeWithId } from '@salescore/core'
import { getInnerRowSpan } from '@salescore/core'
import { Array, Match, Number, Option } from 'effect'
import { isUndefined } from 'effect/Predicate'

import { bindDispatcher, type StateDispatcher, type StateTransformer } from '../binder'
import type { RSheetColumn, RSheetRecordNode, RSheetsColumnMeta, RSheetsColumnType } from '../column'
import { type EdgeType, getNextCursor, TOP_LEFT_CURSOR_POSITION } from './actions/getNextCursor'
import { findLeftColumnIndexForSelectAllCells, getArea, isCursorOverlap } from './cursorHelpers'
import type { RSheetCursorContextMenuPosition, RSheetsCursor, RSheetsCursorPosition } from './cursorTypes'

type Entity = RSheetsCursor | undefined

// 全てのセルを選択
export const selectAllCell = ({
  columns,
  rows,
}: {
  columns: Array<RSheetColumn<RSheetRecordNode>>
  rows: ViewQueryRecordNode[]
}): Entity => {
  const columnIndex = findLeftColumnIndexForSelectAllCells(columns)
  const maxColumnIndex = columns.length - 1
  const maxRowIndex = rows.length - 1

  return {
    main: {
      rowIndex: 0,
      columnIndex,
      innerRowIndex: 0,
    },
    expand: {
      rowIndex: maxRowIndex,
      columnIndex: maxColumnIndex,
      innerRowIndex: 0,
    },
    editing: undefined,
  }
}

export const clearCursor = (): Entity => undefined

// 1行分のセルを選択
export const selectRowCell = ({
  rowIndex,
  rootRecordNode,
  columns,
}: {
  rowIndex: number
  rootRecordNode: RSheetRecordNode
  columns: Array<RSheetColumn<RSheetRecordNode>>
}): StateTransformer<Entity> => {
  // 実用的に、左端のシステム系カラムやレコードURLは不要なはずなので、これを除く
  const ignoreColumnTypes = ['rowIndex', 'open', 'checkbox'] as RSheetsColumnType[]
  const ignoreColumnMetaTypes = ['url', 'record_url'] as RSheetsColumnMeta[]
  const maxColumnIndex = columns.length - 1
  const columnIndex = columns.findIndex(
    (c) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      c.isSystem !== true && !ignoreColumnTypes.includes(c.type!) && !ignoreColumnMetaTypes.includes(c.metaType!),
  )

  return (cursor) => ({
    ...cursor,
    main: { rowIndex, columnIndex, innerRowIndex: 0 },
    expand: { rowIndex, columnIndex: maxColumnIndex, innerRowIndex: getInnerRowSpan(rootRecordNode) - 1 },
    editing: undefined,
  })
}

const _finishEditMode: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return
  }

  return {
    ...previous,
    editing: undefined,
  }
}

const finishEditMode = (): StateTransformer<Entity> => _finishEditMode

const _moveToAboveInnerRow: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return previous
  }
  return {
    ...previous,
    main: {
      ...previous.main,
      innerRowIndex: Math.max(0, previous.main.innerRowIndex - 1),
    },
    contextMenuPosition: undefined,
  }
}
const moveToAboveInnerRow = (): StateTransformer<Entity> => _moveToAboveInnerRow

const setMainCursor =
  (position: RSheetsCursorPosition): StateTransformer<Entity> =>
  (previous) => ({
    main: position,
    expand: undefined,
    editing: undefined,
    contextMenuPosition: undefined,
    copying: previous?.copying,
  })

const setContextMenuPosition = (
  position: RSheetsCursorPosition,
  contextMenuPosition: RSheetCursorContextMenuPosition,
): Entity => ({
  main: position,
  expand: undefined,
  editing: undefined,
  contextMenuPosition,
})

const _hideContextMenu: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return
  }
  return {
    ...previous,
    contextMenuPosition: undefined,
  }
}
const hideContextMenu = (): StateTransformer<Entity> => _hideContextMenu

const _hideCopyingArea: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return
  }
  if (previous.copying === undefined) {
    return previous
  }

  return {
    ...previous,
    copying: {
      ...previous.copying,
      visible: false,
    },
  }
}
const hideCopyingArea = (): StateTransformer<Entity> => _hideCopyingArea

const moveToRowIndexAndEditMode =
  (rowIndex: number, columns: Array<RSheetColumn<RSheetRecordNode>>): StateTransformer<Entity> =>
  // 変な形で編集モードになるケースが多いため、いったん編集モードはやめる
  (oldCursor) => {
    if (oldCursor === undefined) {
      const firstWritableColumnIndex = Math.max(
        columns.findIndex((x) => x.readonly !== false),
        0,
        // eslint-disable-next-line sonarjs/todo-tag
      ) // TODO

      return {
        main: {
          rowIndex,
          columnIndex: firstWritableColumnIndex,
          innerRowIndex: 0,
        },
        // isEditing: column?.readonly !== true,
        isEditing: false,
      }
    }

    return {
      main: {
        ...oldCursor.main,
        rowIndex,
      },
      // isEditing: column?.readonly !== true,
      isEditing: false,
    }
  }

const _onAfterCopyCell: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return previous
  }
  return {
    ...previous,
    copying: {
      main: previous.main,
      expand: previous.expand,
      visible: true,
    },
  }
}

const onAfterCopyCell = (): StateTransformer<Entity> => _onAfterCopyCell

const onClickCurrentCursor =
  (column: RSheetColumn<RSheetRecordNode>, pressedKey?: string): StateTransformer<Entity> =>
  (previous) => {
    if (previous === undefined) {
      return previous
    }
    return {
      ...previous,
      contextMenuPosition: undefined,
      editing: {
        isEditing: column.readonly === true ? undefined : previous.editing?.isEditing !== true,
        initialPressedKey: pressedKey,
      },
    }
  }

const onAfterToggleCheckbox =
  (newMainCursor: RSheetsCursorPosition, keepExpand?: boolean): StateTransformer<Entity> =>
  (previous) => ({
    main: newMainCursor,
    expand: keepExpand === true ? previous?.expand : undefined,
    editing: undefined,
    contextMenuPosition: undefined, // これでよいか？
    copying: undefined, // これでよいか？
  })

const _moveToNextInnerRow: StateTransformer<Entity> = (previous) => {
  if (previous === undefined) {
    return previous
  }
  return {
    main: {
      ...previous.main,
      innerRowIndex: previous.main.innerRowIndex + 1,
    },
    copying: previous.copying,
    contextMenuPosition: previous.contextMenuPosition,
  }
}
const moveToNextInnerRow = (): StateTransformer<Entity> => _moveToNextInnerRow

const moveToRowIndex =
  (rowIndex: number): StateTransformer<Entity> =>
  (previous) => {
    if (previous === undefined) {
      return previous
    }
    return {
      main: {
        ...previous.main,
        rowIndex,
      },
      copying: previous.copying,
      contextMenuPosition: previous.contextMenuPosition,
    }
  }

const moveToInnerRowIndex =
  (innerRowIndex: number): StateTransformer<Entity> =>
  (previous) => {
    if (previous === undefined) {
      return previous
    }
    return {
      main: {
        ...previous.main,
        innerRowIndex,
      },
      copying: previous.copying,
      contextMenuPosition: previous.contextMenuPosition,
    }
  }

const onSetMainCursor =
  (
    columns: Array<RSheetColumn<RSheetRecordNode>>,
    newMainCursor: RSheetsCursorPosition,
    isEditing: boolean,
    option?: { keepExpand?: boolean; contextMenuPosition?: RSheetCursorContextMenuPosition },
  ): StateTransformer<Entity> =>
  (previous) => {
    const overlap = isCursorOverlap(previous, newMainCursor)
    if (
      option?.keepExpand === true &&
      previous?.expand !== undefined &&
      (overlap.isExpandSelected || overlap.isMainSelected || overlap.isRangeSelected)
    ) {
      const { leftColumnIndex: left, rightColumnIndex: right } = getArea(previous.main, previous.expand)
      const objectSet = new Set<string>()
      for (const columnIndex of left <= right ? Array.range(left, right) : Array.empty<number>()) {
        const column = columns[columnIndex]
        if (column === undefined) {
          continue
        }
        objectSet.add(column.node.name)
      }
      if (objectSet.size === 1) {
        // 選択範囲に単一オブジェクトのカラムしか入っていない場合

        return {
          ...previous,
          contextMenuPosition: option.contextMenuPosition,
        }
      }
    }
    return {
      main: newMainCursor,
      expand: undefined,
      // 読み取り専用カラムでも編集モードになってしまう可能性があるように見えるが、
      //  呼び出し元のuseSetMainCursorByRecordIdにて、
      //  isEditingがtrueの際はcolumn.readonlyがtrueでないことを検証しているため、ここでの検証は省略する
      editing: isEditing ? { isEditing: true } : undefined,
      contextMenuPosition: option?.contextMenuPosition,
      copying: previous?.copying,
    }
  }

const moveToEdge =
  ({
    rows,
    columns,
    type,
    expand,
  }: {
    rows: ViewQueryRecordNode[]
    columns: Array<RSheetColumn<RSheetRecordNode>>
    type: EdgeType
    expand: boolean
  }): StateTransformer<Entity> =>
  (previous) =>
    getNextCursor({
      cursor: previous,
      columns,
      rows,
      type,
      expand,
    })

const expand =
  (newMainCursor: RSheetsCursorPosition): StateTransformer<Entity> =>
  (previous) =>
    isUndefined(previous)
      ? {
          main: newMainCursor,
        }
      : {
          ...previous,
          expand: newMainCursor,
          editing: undefined,
          contextMenuPosition: undefined,
        }

export const expandWithDiff =
  (
    { dx, dy }: { dx: 1 | -1 | 0; dy: 1 | -1 | 0 },
    rows: ViewQueryRecordNodeWithId[],
    columns: Array<RSheetColumn<RSheetRecordNode>>,
    getInnerCells: (row: ViewQueryRecordNode, column: RSheetColumn<RSheetRecordNode>) => SheetCell[],
  ): StateTransformer<Entity> =>
  (previous) => {
    if (isUndefined(previous)) return previous

    const { expand: expandCursor } = previous
    if (isUndefined(expandCursor)) return previous

    const getInnerCellsCount = (rowIndex: number, columnIndex: number): number =>
      Option.zipWith(Array.get(rows, rowIndex), Array.get(columns, columnIndex), getInnerCells).pipe(
        Option.getOrElse(() => []),
      ).length

    const columnIndex = Number.clamp(expandCursor.columnIndex + dx, {
      minimum: TOP_LEFT_CURSOR_POSITION.columnIndex,
      maximum: columns.length - 1,
    })
    const innerCellsCount = getInnerCellsCount(expandCursor.rowIndex, columnIndex)
    const maxInnerRowIndex = innerCellsCount === 0 ? 0 : innerCellsCount - 1
    const { rowIndex, innerRowIndex } = Match.value({ ...expandCursor, dy }).pipe(
      // 一番上の行から上方向へは移動しない
      Match.when(
        ({ dy, rowIndex, innerRowIndex }) =>
          dy < 0 &&
          rowIndex === TOP_LEFT_CURSOR_POSITION.rowIndex &&
          innerRowIndex === TOP_LEFT_CURSOR_POSITION.innerRowIndex,
        () => expandCursor,
      ),
      // 一番下の行から下方向へは移動しない
      Match.when(
        ({ dy, rowIndex, innerRowIndex }) =>
          dy > 0 && rowIndex === rows.length - 1 && innerRowIndex === maxInnerRowIndex,
        () => expandCursor,
      ),
      Match.orElse(({ innerRowIndex, dy }) =>
        Match.value(innerRowIndex + dy).pipe(
          Match.when(Number.isLessThan(0), () => ({
            // 一番上のインナー行から上方向への移動は1つ上の行にある一番下のインナー行とする
            rowIndex: expandCursor.rowIndex - 1,
            innerRowIndex: getInnerCellsCount(expandCursor.rowIndex - 1, columnIndex) - 1,
          })),
          Match.when(Number.isGreaterThanOrEqualTo(innerCellsCount), () => ({
            // 一番下のインナー行から下方向への移動は1つ下の行にある一番上のインナー行とする
            rowIndex: expandCursor.rowIndex + 1,
            innerRowIndex: 0,
          })),
          Match.orElse((innerRowIndex) => ({ rowIndex: expandCursor.rowIndex, innerRowIndex })),
        ),
      ),
    )

    return expand({ rowIndex, columnIndex, innerRowIndex })(previous)
  }

export const bindCursorActions = (dispatcher: StateDispatcher<Entity>) => {
  const bind = bindDispatcher(dispatcher)
  return {
    selectAllCell: bind(selectAllCell),
    clearCursor: bind(clearCursor),
    selectRowCell: bind(selectRowCell),
    finishEditMode: bind(finishEditMode),
    moveToAboveInnerRow: bind(moveToAboveInnerRow),
    moveToNextInnerRow: bind(moveToNextInnerRow),
    moveToRowIndex: bind(moveToRowIndex),
    moveToInnerRowIndex: bind(moveToInnerRowIndex),
    setMainCursor: bind(setMainCursor),
    setContextMenuPosition: bind(setContextMenuPosition),
    hideContextMenu: bind(hideContextMenu),
    hideCopyingArea: bind(hideCopyingArea),
    moveToRowIndexAndEditMode: bind(moveToRowIndexAndEditMode),
    onAfterCopyCell: bind(onAfterCopyCell),
    onClickCurrentCursor: bind(onClickCurrentCursor),
    onAfterToggleCheckbox: bind(onAfterToggleCheckbox),
    onSetMainCursor: bind(onSetMainCursor),
    moveToEdge: bind(moveToEdge),
    expand: bind(expand),
    expandWithDiff: bind(expandWithDiff),
  }
}
